import { useEffect, useMemo, useState } from "react";
import { Search } from "lucide-react";
import { Modal } from "@/components/ui/Modal";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Spinner } from "@/components/ui/Spinner";
import { useRecipientOptions, useSendSystemMessage } from "../hooks/useSystemMessages";
import type { RecipientOption } from "../types";

// Bez znaków diakrytycznych, żeby „kowalski" znajdowało „Kowalską".
const normalize = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ł/g, "l");

const NO_BRANCH = "Bez oddziału";

interface Props {
  open: boolean;
  onClose: () => void;
}

/** Nowa wiadomość systemowa: treść + wybór pracowników.
 *
 *  Lista do wyboru przychodzi z backendu już zawężona do oddziałów, które nadawca
 *  widzi (CEO — wszystkie), więc „zaznacz wszystkich" nigdy nie sięga dalej niż
 *  jego uprawnienia. Przy pełnym zaznaczeniu wysyłamy `send_to_all`, żeby zestaw
 *  odbiorców ustalił serwer w chwili wysyłki, a nie przeglądarka. */
export function ComposeSystemMessageModal({ open, onClose }: Props) {
  const { data: options = [], isLoading } = useRecipientOptions(open);
  const send = useSendSystemMessage();

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setTitle("");
    setBody("");
    setQuery("");
    setSelected(new Set());
    setError(null);
  }, [open]);

  const visible = useMemo(() => {
    const q = normalize(query.trim());
    if (!q) return options;
    return options.filter((o) =>
      normalize(`${o.name} ${o.job_title} ${o.role_label} ${o.branch_name ?? ""}`).includes(q),
    );
  }, [options, query]);

  const groups = useMemo(() => {
    const map = new Map<string, RecipientOption[]>();
    for (const option of visible) {
      const key = option.branch_name ?? NO_BRANCH;
      const bucket = map.get(key);
      if (bucket) bucket.push(option);
      else map.set(key, [option]);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b, "pl"));
  }, [visible]);

  const filtering = query.trim().length > 0;
  const allVisibleSelected =
    visible.length > 0 && visible.every((o) => selected.has(o.id));

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function setMany(ids: string[], on: boolean) {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }

  function submit() {
    if (!title.trim() || !body.trim()) {
      setError("Uzupełnij tytuł i treść wiadomości.");
      return;
    }
    if (selected.size === 0) {
      setError("Wskaż co najmniej jednego odbiorcę.");
      return;
    }
    setError(null);
    send.mutate(
      {
        title: title.trim(),
        body: body.trim(),
        recipient_ids: [...selected],
        send_to_all: selected.size === options.length,
      },
      {
        onSuccess: onClose,
        onError: () => setError("Nie udało się wysłać wiadomości."),
      },
    );
  }

  return (
    <Modal open={open} onClose={onClose} title="Nowa wiadomość systemowa" size="lg">
      <div className="flex flex-col gap-4 px-6 py-4">
        <Input
          label="Tytuł"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="np. Ustalenia z narady"
        />

        <div className="flex flex-col gap-1">
          <label
            htmlFor="system-message-body"
            className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]"
          >
            Treść
          </label>
          <textarea
            id="system-message-body"
            rows={5}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Kluczowe ustalenia lub wnioski…"
            className="input-base resize-y"
          />
          <p className="text-xs text-[var(--color-text-secondary)]">
            Wiadomość zablokuje ekran odbiorcy na 30 sekund i zniknie dopiero po
            kliknięciu „Oznacz jako przeczytane".
          </p>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
              Odbiorcy — wybrano {selected.size} z {options.length}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setMany(visible.map((o) => o.id), !allVisibleSelected)}
                className="text-xs underline text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
              >
                {allVisibleSelected
                  ? filtering
                    ? "Odznacz widocznych"
                    : "Odznacz wszystkich"
                  : filtering
                    ? "Zaznacz widocznych"
                    : "Zaznacz wszystkich"}
              </button>
            </div>
          </div>

          <div className="relative">
            <Search
              size={14}
              className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-[var(--color-text-muted)]"
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Szukaj pracownika…"
              className="input-base pl-7"
            />
          </div>

          <div className="max-h-64 overflow-y-auto border border-[var(--color-border)]">
            {isLoading ? (
              <div className="flex justify-center py-6">
                <Spinner />
              </div>
            ) : visible.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-[var(--color-text-secondary)]">
                {options.length === 0
                  ? "Brak pracowników w oddziałach, które widzisz."
                  : "Nikt nie pasuje do wyszukiwania."}
              </p>
            ) : (
              groups.map(([branchName, people]) => {
                const ids = people.map((p) => p.id);
                const allInBranch = people.every((p) => selected.has(p.id));
                return (
                  <div key={branchName}>
                    <div className="flex items-center justify-between gap-2 bg-[var(--color-bg-base)] px-3 py-1.5">
                      <span className="text-xs font-mono uppercase tracking-wider text-[var(--color-text-muted)]">
                        {branchName} ({people.length})
                      </span>
                      <button
                        type="button"
                        onClick={() => setMany(ids, !allInBranch)}
                        className="text-xs underline text-[var(--color-text-secondary)] hover:text-[var(--color-primary)]"
                      >
                        {allInBranch ? "odznacz" : "zaznacz"}
                      </button>
                    </div>
                    {people.map((person) => (
                      <label
                        key={person.id}
                        className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-[var(--color-bg-base)]"
                      >
                        <input
                          type="checkbox"
                          checked={selected.has(person.id)}
                          onChange={() => toggle(person.id)}
                        />
                        <span className="text-sm text-[var(--color-text-primary)]">
                          {person.name}
                        </span>
                        <span className="text-xs text-[var(--color-text-secondary)]">
                          {person.job_title || person.role_label}
                        </span>
                      </label>
                    ))}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {error && (
          <p className="text-sm text-[var(--color-primary)]">{error}</p>
        )}

        <div className="flex justify-end gap-2">
          <Button type="button" variant="ghost" onClick={onClose}>
            Anuluj
          </Button>
          <Button type="button" loading={send.isPending} onClick={submit}>
            Wyślij ({selected.size})
          </Button>
        </div>
      </div>
    </Modal>
  );
}
